import { n as encodeBase64Url, t as decodeBase64Url } from "./base64url-CsUtSmys.js";
import { n as utf8Encode, t as utf8DecodeTotal } from "./utf8--X9rtiRi.js";
//#region ../src/utils/qr-payload/compression.ts
/**
* Thin `CompressionStream`/`DecompressionStream` helpers for the QR payload
* codecs (benchmark plan §6 P3). Runtime floor: browsers since Safari 16.4 /
* Chrome 103 / Firefox 113; Node ≥ 21.2 for `'deflate-raw'` — hence the
* package's `engines: >=22` (decision D3).
*
* `compressBytes` may reject only on programming errors (unknown format);
* `decompressBytes` is TOTAL over its byte input — corrupt streams yield
* `null`, never a throw, because printed QR codes deliver damaged payloads
* forever.
*/
async function compressBytes(bytes, format) {
	return pipeBytesThrough(bytes, new CompressionStream(format));
}
async function decompressBytes(bytes, format) {
	try {
		return await pipeBytesThrough(bytes, new DecompressionStream(format));
	} catch {
		return null;
	}
}
async function pipeBytesThrough(bytes, transform) {
	const writer = transform.writable.getWriter();
	const writing = writer.write(new Uint8Array(bytes)).then(() => writer.close());
	const reading = new Response(transform.readable).arrayBuffer();
	const [, buffer] = await Promise.all([writing, reading]);
	return new Uint8Array(buffer);
}
//#endregion
//#region ../src/utils/qr-payload/codec-dictionary.ts
/**
* Codec A4 of the QR payload-compression benchmark (plan §4): a VERSIONED
* static substitution dictionary — known long substrings (URL prefixes,
* JSON envelope keys) collapse to single reserved bytes (0x01–0x1F) before
* transport encoding. Hypothesis H2: on short URL pointers this beats
* generic deflate, which cannot amortise its header on < 150 chars.
*
* Wire layout: `[versionByte][body…]`, base64url-transported. Printed QR
* codes are immutable, so the table may only EVOLVE by adding a new version
* byte with a new table — decode must support every version ever printed
* (currently: 0x01). Literal control bytes in the payload are escaped with
* 0x00 so they can never be misread as tokens.
*
* Also exports the A4+A2 chain (dictionary, then `deflate-raw`): the
* substituted bytes are what generic compression sees, so the two compose.
*/
const DICTIONARY_VERSION = 1;
const ESCAPE = 0;
/**
* Token table, version 0x01. Bytes 0x01–0x1F are reserved for tokens (31
* slots; 23 used). Sorted longest-first at module load for greedy matching —
* entry order here is only cosmetic, BUT the byte assignments are frozen
* forever once a QR code is printed.
*/
const TOKEN_TABLE_V1 = [
	[1, "https://raw.githubusercontent.com/"],
	[2, "https://gps.csutil.com/"],
	[3, "https://drive.google.com/file/d/"],
	[4, "https://drive.google.com/"],
	[5, "https://github.com/"],
	[6, "https://"],
	[7, "/refs/heads/main/"],
	[8, "/main/"],
	[9, ".json"],
	[10, ".github.io/"],
	[11, "?usp=sharing"],
	[12, "/view"],
	[13, "{\"a\":[{\"lat\":"],
	[14, ",\"lon\":"],
	[15, ",\"alt\":"],
	[16, ",\"n\":\""],
	[17, ",\"ui\":"],
	[18, ",\"s\":"],
	[19, ",\"r\":"],
	[20, "},{\"lat\":"],
	[21, "}]}"],
	[22, "\"}"],
	[23, ".zip"]
];
const TOKENS_BY_LENGTH = [...TOKEN_TABLE_V1].sort((a, b) => b[1].length - a[1].length);
const TOKEN_BYTES_BY_VALUE = new Map(TOKEN_TABLE_V1.map(([byte, text]) => [byte, utf8Encode(text)]));
/** Substitute known substrings and prepend the dictionary version byte. */
function packDictionaryBytes(payload) {
	const out = [DICTIONARY_VERSION];
	let index = 0;
	while (index < payload.length) {
		const token = matchTokenAt(payload, index);
		if (token !== null) {
			out.push(token[0]);
			index += token[1].length;
			continue;
		}
		index += pushLiteralChar(payload, index, out);
	}
	return Uint8Array.from(out);
}
function matchTokenAt(payload, index) {
	for (const token of TOKENS_BY_LENGTH) if (payload.startsWith(token[1], index)) return token;
	return null;
}
/** Append one code point's UTF-8 bytes (escaped if < 0x20); returns its
* length in UTF-16 units so the caller can advance. */
function pushLiteralChar(payload, index, out) {
	const char = String.fromCodePoint(payload.codePointAt(index) ?? 0);
	for (const byte of utf8Encode(char)) {
		if (byte < 32) out.push(ESCAPE);
		out.push(byte);
	}
	return char.length;
}
/** Expand a packed dictionary body. Total: unknown version/token → `null`. */
function unpackDictionaryBytes(bytes) {
	if (bytes.length === 0 || bytes[0] !== DICTIONARY_VERSION) return null;
	const out = [];
	for (let i = 1; i < bytes.length; i++) {
		const byte = bytes[i] ?? 0;
		if (byte === ESCAPE) {
			i += 1;
			if (i >= bytes.length) return null;
			out.push(bytes[i] ?? 0);
		} else if (byte < 32) {
			const expansion = TOKEN_BYTES_BY_VALUE.get(byte);
			if (expansion === void 0) return null;
			out.push(...expansion);
		} else out.push(byte);
	}
	return utf8DecodeTotal(Uint8Array.from(out));
}
/**
* A4: dictionary substitution + base64url. Promise-returning (not `async` —
* the work is synchronous) so all benchmark codecs share one signature.
*/
function encodeDictionaryPayload(payload) {
	return Promise.resolve(encodeBase64Url(packDictionaryBytes(payload)));
}
function decodeDictionaryPayload(text) {
	const bytes = decodeBase64Url(text);
	return Promise.resolve(bytes === null ? null : unpackDictionaryBytes(bytes));
}
/** A4+A2 chain: dictionary substitution, then deflate-raw, then base64url. */
async function encodeDictionaryDeflatePayload(payload) {
	return encodeBase64Url(await compressBytes(packDictionaryBytes(payload), "deflate-raw"));
}
async function decodeDictionaryDeflatePayload(text) {
	const bytes = decodeBase64Url(text);
	if (bytes === null) return null;
	const inflated = await decompressBytes(bytes, "deflate-raw");
	if (inflated === null) return null;
	return unpackDictionaryBytes(inflated);
}
//#endregion
export { packDictionaryBytes as a, encodeDictionaryPayload as i, decodeDictionaryPayload as n, compressBytes as o, encodeDictionaryDeflatePayload as r, decodeDictionaryDeflatePayload as t };
